import { Box, Button, Typography } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import useMSSP from "../../../../../api/hooks/useMSSP";
import { entityKeys } from "../../../../../api/queryKeys";
import ExportButton from "../../../../../components/buttons/ExportButton";
import { EnvFeatureFlagContext } from "../../../../../contexts/EnvFeaturesContext";
import { useFileNaming } from "../../../../../hooks/useFileNaming";
import { QueryParam } from "../../../../../utils/constants";
import ColumnSettingsModal, {
  getVisibleColumnsForEntity,
} from "../../components/ColumnSettingsModal";
import EditLabelsButton from "../../components/EditLabelsButton";
import EditTagsButton from "../../components/EditTagsButton";
import EntityDrawer from "../../components/EntityDrawer/EntityDrawer";
import MarkAsCriticalButton from "../../components/MarkAsCriticalButton/MarkAsCriticalButton";
import EntitiesTable from "../../entitiesTable/EntitiesTable";
import { addRawDataToEntities } from "../../entitiesTable/fieldUtils";
import { useEntities } from "../../hooks/useEntities";

export const ALL_ENTITIES = "all_entities";

export default function EntitiesOverviewTableView({
  filteredEntities,
  assets,
  isSearching = false,
  visibleColumns,
  setVisibleColumns,
  useQueryForAllEntities,
}) {
  const colors = useColors();
  const { fetchRawDataFields } = useAPI();
  const gridRef = useGridApiRef();
  const { trackEvent, fetchEntityById } = useAPI();
  const { flattenEntity } = useEntities();
  const [searchParams, setSearchParams] = useSearchParams();
  const [checkedEntityIDs, setCheckedEntityIDs] = useState([]);
  const { generateFileName } = useFileNaming();
  const { hasMultipleSelectedTenants } = useMSSP();
  const { isActiveRds } = useContext(EnvFeatureFlagContext);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);

  const handleTableExport = () => {
    trackEvent("User click on 'Export' button to download the inventory table");
    const csvExportOptions = {
      fileName: generateFileName(`inventory`),
      allColumns: true,
    };
    gridRef?.current?.exportDataAsCsv(csvExportOptions);
  };

  const onDrawerClose = () => {
    const updatedSearchParams = new URLSearchParams(searchParams);
    updatedSearchParams.delete(QueryParam.entity);
    setSearchParams(updatedSearchParams);
    trackEvent("User closed the entity drawer");
  };

  const handleOpenColumnSettings = () => {
    trackEvent("User opened entity column settings");
    setColumnSettingsOpen(true);
  };

  const handleColumnsChange = (newSelectedColumns) => {
    setVisibleColumns(newSelectedColumns);
    trackEvent("User updated entity column visibility");
  };

  useEffect(() => {
    if (isActiveRds) {
      const savedColumns = getVisibleColumnsForEntity(ALL_ENTITIES);
      setVisibleColumns(savedColumns);
    } else {
      setVisibleColumns(null);
    }
  }, [isActiveRds, setVisibleColumns]);

  const viewedEntityUniqueId = decodeURIComponent(
    searchParams.get(QueryParam.entity)
  );

  const entityFromTable = viewedEntityUniqueId
    ? filteredEntities?.find((row) => row.unique_id === viewedEntityUniqueId)
    : null;

  const {
    data: fetchedEntity,
    isLoading: entityDetailsIsLoading,
    isFetching: entityDetailsIsFetching,
  } = useQuery({
    queryKey: entityKeys.entity(entityFromTable?.entity_id || ""),
    queryFn: () => fetchEntityById(entityFromTable?.entity_id),
    enabled: !!entityFromTable?.entity_id && !isActiveRds, // RDS environment doesn't have the entity endpoint
    select: (response) => {
      if (!response?.data) return null;
      return flattenEntity(response.data);
    },
  });

  const viewedEntity = fetchedEntity || entityFromTable;

  const rawDataFieldsQuery = useQuery({
    queryKey: entityKeys.entitiesRawData(visibleColumns),
    queryFn: () => fetchRawDataFields(visibleColumns),
    enabled: Boolean(
      isActiveRds &&
        visibleColumns &&
        visibleColumns.length > 0 &&
        useQueryForAllEntities.isSuccess
    ),
    select: (response) => response.data,
  });

  // TODO - this may be a real case for useMemo (unlike the rest)
  if (rawDataFieldsQuery.isSuccess)
    addRawDataToEntities(filteredEntities, rawDataFieldsQuery.data);

  const checkedEntities = filteredEntities.filter((entity) =>
    checkedEntityIDs.includes(entity.entity_id + "-" + entity.customer_id)
  );
  const checkedEntitiesIds = checkedEntities.map((entity) => entity.entity_id);
  return (
    <>
      <Box display="flex" flexDirection="column" gap={2}>
        <Box display="flex" justifyContent="flex-end">
          <Box display="flex" gap={1}>
            {!hasMultipleSelectedTenants && (
              <>
                <EditLabelsButton checkedEntities={checkedEntities} />
                <EditTagsButton checkedEntities={checkedEntities} />
                <MarkAsCriticalButton
                  checkedEntityIDs={checkedEntitiesIds}
                  assets={assets}
                />
              </>
            )}
            {isActiveRds && (
              <Button
                onClick={handleOpenColumnSettings}
                sx={{
                  color: colors.text_primary,
                  height: "30px",
                  border: "1px solid " + colors.grey_48percent,
                  textTransform: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                  background: "white",
                }}
              >
                <Typography variant="overline">Columns</Typography>
              </Button>
            )}
            <ExportButton
              handleExport={handleTableExport}
              sx={{
                border: "1px solid " + colors.grey_48percent,
                background: "white",
              }}
            />
          </Box>
        </Box>
        <EntitiesTable
          entityType={ALL_ENTITIES}
          entitiesAreLoading={
            isSearching ||
            useQueryForAllEntities.isFetching ||
            rawDataFieldsQuery.isLoading
          }
          entitiesAreFetching={false}
          filteredEntities={filteredEntities}
          setCheckedEntityIDs={setCheckedEntityIDs}
          fetchedEntities={filteredEntities}
          assets={assets}
          gridRef={gridRef}
          visibleColumns={visibleColumns}
          sx={{
            '& .MuiDataGrid-columnHeader[data-field="__check__"]': {
              width: "80px !important",
              minWidth: "80px !important",
              maxWidth: "80px !important",
            },
            '& .MuiDataGrid-cell[data-field="__check__"]': {
              width: "80px !important",
              minWidth: "80px !important",
              maxWidth: "80px !important",
            },
          }}
        />
      </Box>
      <EntityDrawer
        openDrawer={viewedEntity}
        selectedEntity={viewedEntity}
        entityDetailsIsLoading={
          Boolean(entityFromTable?.entity_id && !isActiveRds) &&
          (entityDetailsIsLoading || entityDetailsIsFetching)
        }
        onClose={onDrawerClose}
      />

      {isActiveRds && (
        <ColumnSettingsModal
          entities={filteredEntities}
          open={columnSettingsOpen}
          onClose={() => setColumnSettingsOpen(false)}
          entityType={ALL_ENTITIES}
          onColumnsChange={handleColumnsChange}
        />
      )}
    </>
  );
}
